/*
 * fof/upload「文本文档预览」救活补丁（1.8.36.37）
 *
 * 【为什么需要这个补丁】
 * fof/upload 的 text-preview 模板（text-preview.blade.php）把「展开全文」的
 * 点击监听写在 figure 里的【内联 <script>】中。而 Flarum 渲染帖子正文走的是
 * CommentPost.js 的 m.trust(post.contentHtml()) —— 即 innerHTML 注入，
 * 浏览器【永远不会执行 innerHTML 里的 <script>】。结果：
 *   - 「展开全文」按钮看得见（CSS 按 data-hassnippet 控制显隐）
 *   - 但点击毫无反应（事件监听根本没绑上）
 * 这是 fof/upload 在 Flarum SPA 下的设计缺陷，与帖子新旧无关。
 *
 * 另外：摘要文字本应由服务端 FormatTextPreview 渲染进 <code>{@snippet}</code>，
 * 但线上服务端 snippet 经常为空（文件下载/CDN/opcache 等种种原因），导致初始
 * 预览框是空的——必须点「展开全文」再点「收起全文」才看到字。本补丁顺手在
 * figure 出现时自动 fetch 一次、把前 5 行填进摘要，用户无需任何点击即可看到预览。
 *
 * 【方案】document 级事件委托 + MutationObserver，不碰 fof/upload 的任何文件：
 *   1. MutationObserver 监听 .FofUpload-TextPreview 注入，自动拉取文件并填充摘要
 *      （仅当服务端没给摘要时才补，已有摘要则不浪费请求）
 *   2. 捕获对 .FofUpload-TextPreviewToggle 的点击：fetch 全文 → 填充
 *      .FofUpload-TextPreviewFull；同源校验；跨域/失败置 data-error
 *   3. 展开/收起、loading、error 状态全部沿用 fof/upload 的 data-* 约定
 *      （CSS 都是现成的，无需自己写样式）
 *
 * 【1.8.36.38 增强】展开全文支持 Markdown 渲染。
 * fof/upload 原逻辑把全文塞进 <pre><code> 当纯文本显示，#、**、列表等语法全裸奔。
 * 改为用 marked 渲染 + DOMPurify 净化后注入；wrapper 加 Post-body 类复用 Flarum 原生
 * markdown 样式，并注入一小段作用域 CSS 抵消 fof/upload 给 inline code 加 12px padding 的副作用。
 * 仅影响「展开全文」区域；摘要（前 5 行）仍是纯文本，保持轻量。
 *
 * 【1.8.36.39 增强】渲染出的 markdown 引用块（blockquote）点击即复制引用内容（带「已复制」角标）。
 * 【1.8.36.40 增强】引用复制作用域从「仅 md 预览区」扩到「整个帖子正文 .Post-body」，
 *   即普通帖子里用 > 写的引用块也能点复制；并兼容 Flarum 原生「引用某帖」结构
 *   （blockquote.Post-quote > .QuotePost），点复制时只取正文、不把“@user wrote:”一起复制，
 *   且点击链接/回复按钮不触发复制（交原生行为处理）。
 *
 * 【1.8.36.41 调整】复制成功的反馈从「引用块角落小角标」改为「居中弹出提示框（toast）」，
 *   1.2s 后自动淡出，无需手动关闭；样式由 .AcmeCopyToast / .AcmeCopyToast--show 控制。
 */

import { marked } from 'marked';
import DOMPurify from 'dompurify';

const URL_RE = /new URL\('([^']+)'\)/;

function codeBlock(text) {
  // 与原模板 createCodeHtml 一致：textContent 塞入，杜绝 XSS
  const code = document.createElement('code');
  code.innerText = text;
  const pre = document.createElement('pre');
  pre.appendChild(code);
  return pre;
}

function firstLines(text, n = 5) {
  return text
    .split(/\r\n|\r|\n/)
    .filter((l) => l.trim() !== '')
    .slice(0, n)
    .join('\n');
}

// 注入一次作用域 CSS：抵消 fof/upload 给 .FofUpload-TextPreviewFull code 加 12px padding 的副作用，
// 并给 markdown 块级元素（引用/表格/代码块）一套干净的兜底样式。
let acmeMdStyleInjected = false;
function ensureMdStyle() {
  if (acmeMdStyleInjected) return;
  acmeMdStyleInjected = true;
  const s = document.createElement('style');
  s.id = 'acme-md-preview-style';
  s.textContent = [
    '.AcmeMdPreview { font-size: 14px; line-height: 1.6; }',
    '.AcmeMdPreview > :first-child { margin-top: 0; }',
    '.AcmeMdPreview > :last-child { margin-bottom: 0; }',
    // 覆盖 fof/upload 的 .FofUpload-TextPreviewFull code { padding: 12px }（inline code 不该有大内边距）
    '.FofUpload-TextPreviewFull .AcmeMdPreview code { padding: 1px 5px; background: rgba(125,125,125,.16); border-radius: 4px; font-size: 85%; }',
    '.FofUpload-TextPreviewFull .AcmeMdPreview pre { background: rgba(125,125,125,.1); padding: 12px; border-radius: 6px; overflow: auto; }',
    '.FofUpload-TextPreviewFull .AcmeMdPreview pre code { padding: 0; background: none; font-size: 13px; }',
    '.AcmeMdPreview img { max-width: 100%; }',
    '.AcmeMdPreview table { border-collapse: collapse; margin: 8px 0; }',
    '.AcmeMdPreview th, .AcmeMdPreview td { border: 1px solid rgba(125,125,125,.3); padding: 4px 9px; }',
    '.AcmeMdPreview blockquote { border-left: 3px solid rgba(125,125,125,.35); margin: 8px 0; padding: 2px 12px; opacity: .85; cursor: pointer; position: relative; transition: background .15s; }',
    // 1.8.36.40：普通帖子正文（.Post-body）里的引用块也支持点击复制；不覆盖 Flarum 原生边框/透明度
    '.Post-body blockquote { cursor: pointer; position: relative; }',
    '.AcmeMdPreview blockquote:hover { background: rgba(125,125,125,.12); }',
    '.Post-body:not(.AcmeMdPreview) blockquote:hover { background: rgba(125,125,125,.08); }',
    '.AcmeMdPreview blockquote::after, .Post-body:not(.AcmeMdPreview) blockquote::after { content: "点击复制"; position: absolute; top: 4px; right: 8px; font-size: 11px; color: rgba(125,125,125,.6); opacity: 0; transition: opacity .15s; pointer-events: none; }',
    '.AcmeMdPreview blockquote:hover::after, .Post-body:not(.AcmeMdPreview) blockquote:hover::after { opacity: 1; }',
  ].join('\n');
  document.head.appendChild(s);
}

// 复制成功提示框（toast）样式。与 markdown 预览样式解耦，页面加载即注入，
// 否则在「从未展开过全文/从未用过 md 预览」的情况下点引用复制时，toast 渲染出来没有
// 定位样式会“看不见”（表现为点了没反应）。
let acmeToastStyleInjected = false;
function ensureToastStyle() {
  if (acmeToastStyleInjected) return;
  acmeToastStyleInjected = true;
  const s = document.createElement('style');
  s.id = 'acme-copy-toast-style';
  s.textContent = [
    '.AcmeCopyToast { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%) scale(.96); background: rgba(0,0,0,.82); color: #fff; font-size: 14px; padding: 12px 26px; border-radius: 8px; z-index: 99999; opacity: 0; pointer-events: none; transition: opacity .18s ease, transform .18s ease; box-shadow: 0 4px 16px rgba(0,0,0,.3); }',
    '.AcmeCopyToast--show { opacity: 1; transform: translate(-50%,-50%) scale(1); }',
  ].join('\n');
  document.head.appendChild(s);
}

// Markdown 渲染 + XSS 净化。breaks:true 让纯文本笔记里的单换行也成 <br>，更贴近阅读习惯。
function renderMarkdown(text) {
  const html = marked.parse(text, { gfm: true, breaks: true });
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

// 【1.8.36.39】点击引用块（blockquote）直接复制引用内容。
// 1.8.36.40：作用域从 .AcmeMdPreview 扩到整个 .Post-body（含普通帖子正文的 > 引用）。
function getQuoteText(bq) {
  // Flarum 原生「引用某帖」结构：blockquote.Post-quote > .QuotePost（含 @user wrote: 头 + .Post-body 正文）。
  // 只取其中 .Post-body 的引用正文，避免把“wrote:”头也复制进去。
  if (bq.classList.contains('Post-quote')) {
    const inner = bq.querySelector('.QuotePost .Post-body') || bq.querySelector('.QuotePost');
    if (inner) return inner.innerText.trim();
  }
  return bq.innerText.trim();
}

function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  // 非安全上下文兜底
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      resolve();
    } catch (e) {
      reject(e);
    }
  });
}

// 1.8.36.41：复制成功弹出一个居中的提示框（toast），1.2s 后自动淡出，无需点关闭。
function showCopiedToast() {
  ensureToastStyle();
  let box = document.getElementById('acme-copy-toast');
  if (!box) {
    box = document.createElement('div');
    box.id = 'acme-copy-toast';
    box.className = 'AcmeCopyToast';
    box.textContent = '已复制';
    document.body.appendChild(box);
  }
  box.classList.add('AcmeCopyToast--show');
  clearTimeout(box._acmeTimer);
  box._acmeTimer = setTimeout(() => {
    box.classList.remove('AcmeCopyToast--show');
  }, 1200);
}

function handleBlockquoteClick(e) {
  const bq = e.target.closest('.Post-body blockquote');
  if (!bq) return;
  if (e.target.closest('a, button')) return; // 点链接/回复按钮不复制，交给原生行为
  const sel = window.getSelection();
  if (sel && sel.toString().trim() !== '') return; // 正在选区则不覆盖手动复制
  e.preventDefault();
  const text = getQuoteText(bq);
  if (!text) return;
  copyText(text)
    .then(() => showCopiedToast())
    .catch(() => {});
}

function extractUrl(figure) {
  const script = figure.querySelector('script');
  if (!script) return null;
  const m = script.textContent.match(URL_RE);
  if (!m) return null;
  try {
    return new URL(m[1], location.origin);
  } catch (e) {
    return null;
  }
}

// 拉取一次全文：缓存到 figure._acmeFullText，并把空摘要补上前 5 行。
// 同一 figure 只拉一次（dataset.acmeLoaded 守卫）。
function loadText(figure) {
  if (figure.dataset.acmeLoaded === 'true') return Promise.resolve();
  const url = extractUrl(figure);
  const snippetCode = figure.querySelector('.FofUpload-TextPreviewSnippet code');

  if (!url) {
    figure.setAttribute('data-error', 'true');
    return Promise.resolve();
  }
  // 同源校验（沿用原脚本的安全约束）
  if (url.origin !== location.origin) {
    figure.setAttribute('data-error', 'true');
    console.error('[Acme text-preview] 跨域文件不做预览：', url.href);
    return Promise.resolve();
  }

  return fetch(url.href)
    .then((resp) => {
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      return resp.text();
    })
    .then((text) => {
      figure._acmeFullText = text;
      figure.dataset.acmeLoaded = 'true';

      // 服务端没给摘要时，用全文前 5 行补上（用户无需点击即可看到预览）
      if (snippetCode && snippetCode.textContent.trim() === '') {
        snippetCode.textContent = firstLines(text);
      }
    })
    .catch((err) => {
      figure.setAttribute('data-error', 'true');
      console.group('[Acme text-preview] 加载文本失败');
      console.error(url.href);
      console.error(err);
      console.groupEnd();
    });
}

// 展开时填充 .FofUpload-TextPreviewFull（优先用已缓存的全文，避免二次请求）
// 1.8.36.38：改用 Markdown 渲染（marked + DOMPurify），wrapper 加 Post-body 复用 Flarum 原生样式。
function ensureFull(figure) {
  const fullEl = figure.querySelector('.FofUpload-TextPreviewFull');
  if (!fullEl || fullEl.dataset.loaded === 'true') return;
  if (figure._acmeFullText != null) {
    ensureMdStyle();
    fullEl.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'AcmeMdPreview Post-body';
    wrap.innerHTML = renderMarkdown(figure._acmeFullText);
    fullEl.appendChild(wrap);
    fullEl.dataset.loaded = 'true';
  }
}

function handleClick(e) {
  const btn = e.target.closest('.FofUpload-TextPreviewToggle');
  if (!btn) return;

  const figure = btn.closest('.FofUpload-TextPreview');
  if (!figure) return;

  // 已被 fof/upload 自己的脚本接管（理论上不会发生，防御一下避免双击两份逻辑）
  if (figure.dataset.acmeBound === 'true') return;

  e.preventDefault();
  e.stopPropagation();

  const expanded = figure.getAttribute('data-expanded') === 'true';

  // 已取到全文（初始自动加载或之前点过）→ 直接切换展开/收起
  if (figure.dataset.acmeLoaded === 'true') {
    ensureFull(figure);
    figure.setAttribute('data-expanded', expanded ? 'false' : 'true');
    return;
  }

  figure.setAttribute('data-loading', 'true');

  loadText(figure)
    .finally(() => {
      figure.setAttribute('data-loading', 'false');
      ensureFull(figure);
      const nowExpanded = figure.getAttribute('data-expanded') === 'true';
      figure.setAttribute('data-expanded', nowExpanded ? 'false' : 'true');
    });
}

export function installTextPreviewFix() {
  // 页面加载即注入样式（toast 与 md 预览解耦，不依赖先展开过全文）
  ensureToastStyle();
  ensureMdStyle();
  // 委托在 document 上，动态插入的帖子内容天然覆盖，无需关心重绘时机
  document.addEventListener('click', handleClick, true); // capture：比核心更早拿到点击
  // 1.8.36.39：点击渲染出的 markdown 引用块直接复制内容
  document.addEventListener('click', handleBlockquoteClick);

  // 帖子内容经由 m.trust 注入 DOM，内联 <script> 不执行；
  // 这里用 MutationObserver 在 figure 出现时自动补齐摘要，无需用户点击。
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue; // 只处理元素节点
        const figs =
          node.matches && node.matches('.FofUpload-TextPreview')
            ? [node]
            : node.querySelectorAll
              ? node.querySelectorAll('.FofUpload-TextPreview')
              : [];
        for (const fig of figs) {
          if (fig.dataset.acmeReady === 'true') continue;
          if (fig.getAttribute('data-hassnippet') !== 'true') continue;
          const code = fig.querySelector('.FofUpload-TextPreviewSnippet code');
          // 仅当服务端没给摘要时才自动拉取，避免无谓请求
          if (code && code.textContent.trim() === '') {
            fig.dataset.acmeReady = 'true';
            loadText(fig);
          }
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}
